import { supabase } from './supabase';

/**
 * Review image upload utility
 * Security: File type/size restrictions, safe filename generation
 */

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const BUCKET_NAME = 'review-images';

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

/**
 * File validation
 */
export const validateImageFile = (file: File): string | null => {
  // File type validation
  if (!ALLOWED_TYPES.includes(file.type)) {
    return `Invalid file type. Only JPG, PNG, WEBP, and GIF are allowed. (current: ${file.type})`;
  }

  // File size validation
  if (file.size > MAX_FILE_SIZE) {
    return `File size is too large. Maximum 5MB allowed. (current: ${(file.size / 1024 / 1024).toFixed(2)}MB)`;
  }

  return null; // Valid
};

/**
 * Generate safe filename (UUID-based, removes non-ASCII characters)
 */
const generateSafeFileName = (originalName: string, userId: string): string => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 15);
  const extension = originalName.split('.').pop()?.toLowerCase() || 'jpg';

  // Remove non-ASCII characters (only alphanumeric allowed)
  const safeName = originalName
    .replace(/\.[^/.]+$/, '') // Remove extension
    .replace(/[^a-zA-Z0-9]/g, '') // Only alphanumeric (remove special chars)
    .substring(0, 20); // Max 20 chars

  // Include original name if alphanumeric, otherwise UUID only
  const fileName = safeName.length > 0
    ? `${timestamp}-${randomStr}-${safeName}.${extension}`
    : `${timestamp}-${randomStr}.${extension}`;

  return `${userId}/${fileName}`;
};

/**
 * Upload review image
 */
export const uploadReviewImage = async (
  file: File,
  userId: string
): Promise<UploadResult> => {
  try {
    // 1. File validation
    const validationError = validateImageFile(file);
    if (validationError) {
      return { success: false, error: validationError };
    }

    // 2. Generate safe filename
    const fileName = generateSafeFileName(file.name, userId);
    const filePath = `reviews/${fileName}`;

    console.log('📤 Uploading image:', { fileName, fileSize: file.size, fileType: file.type });

    // 3. Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false // Prevent overwrite
      });

    if (error) {
      console.error('❌ Upload error:', error);
      return { success: false, error: `Upload failed: ${error.message}` };
    }

    // 4. Generate public URL
    const { data: urlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);

    if (!urlData || !urlData.publicUrl) {
      return { success: false, error: 'Failed to generate image URL' };
    }

    console.log('✅ Image uploaded successfully:', urlData.publicUrl);

    return {
      success: true,
      url: urlData.publicUrl
    };
  } catch (error) {
    console.error('❌ Unexpected upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
};

/**
 * Batch upload multiple images
 */
export const uploadMultipleImages = async (
  files: File[],
  userId: string,
  onProgress?: (current: number, total: number) => void
): Promise<UploadResult[]> => {
  const results: UploadResult[] = [];

  for (let i = 0; i < files.length; i++) {
    if (onProgress) {
      onProgress(i + 1, files.length);
    }

    const result = await uploadReviewImage(files[i], userId);
    results.push(result);
  }

  return results;
};

/**
 * Delete image (used on cancellation)
 */
export const deleteReviewImage = async (imageUrl: string): Promise<boolean> => {
  try {
    // Extract file path from URL
    const urlParts = imageUrl.split(`${BUCKET_NAME}/`);
    if (urlParts.length < 2) {
      console.error('Invalid image URL:', imageUrl);
      return false;
    }

    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filePath]);

    if (error) {
      console.error('❌ Delete error:', error);
      return false;
    }

    console.log('✅ Image deleted:', filePath);
    return true;
  } catch (error) {
    console.error('❌ Unexpected delete error:', error);
    return false;
  }
};

// ============================================
// Community image upload
// ============================================

const COMMUNITY_BUCKET = 'community-images';

export const uploadCommunityImage = async (
  file: File,
  userId: string
): Promise<UploadResult> => {
  try {
    const validationError = validateImageFile(file);
    if (validationError) {
      return { success: false, error: validationError };
    }

    const fileName = generateSafeFileName(file.name, userId);
    const filePath = `posts/${fileName}`;

    const { error } = await supabase.storage
      .from(COMMUNITY_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('❌ Community image upload error:', error);
      return { success: false, error: `Upload failed: ${error.message}` };
    }

    const { data: urlData } = supabase.storage
      .from(COMMUNITY_BUCKET)
      .getPublicUrl(filePath);

    if (!urlData || !urlData.publicUrl) {
      return { success: false, error: 'Failed to generate image URL' };
    }

    return { success: true, url: urlData.publicUrl };
  } catch (error) {
    console.error('❌ Community image upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
};

export const deleteCommunityImage = async (imageUrl: string): Promise<boolean> => {
  try {
    const urlParts = imageUrl.split(`${COMMUNITY_BUCKET}/`);
    if (urlParts.length < 2) return false;

    const filePath = urlParts[1];
    const { error } = await supabase.storage
      .from(COMMUNITY_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('❌ Community image delete error:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('❌ Community image delete error:', error);
    return false;
  }
};
